import { useEffect, useState } from 'react'
import { api, type CatalogCandidate } from '../lib/api.ts'
import { Input } from './Input.tsx'
import { Button } from './Button.tsx'

/**
 * Choosing the product a simulated quotation line selected.
 *
 * A simulation exists to exercise the real path, and the real path's central
 * question is whether the product somebody keyed is the one that was
 * prescribed. With no code on the line there is nothing to ask that about, so
 * every ingredient resolved to "the quotation line carries no ERP product
 * code" and the whole resolver stage was simulated as a no-op.
 *
 * Searches through the same `searchCatalog` the resolver is handed, so what
 * gets picked here comes from the same retrieval that decides a real verification --
 * layers, threshold and all.
 *
 * Shows how each candidate was found, because that is the difference between
 * "this is its name" and "somebody once said this means that", and it is the
 * distinction the person picking should be making.
 */

const LAYER_LABEL: Readonly<Record<CatalogCandidate['layer'], string>> = {
  exact: 'nome exato',
  synonym: 'sinônimo',
  trigram: 'aproximado',
}

export interface ProductPickerProps {
  readonly label: string
  /** What the prescription calls it, used as the opening search. */
  readonly suggestion: string
  readonly value: string
  /**
   * The selection, both halves of it.
   *
   * On the quoted side of an verification the ingredient's name *is* the catalogue's
   * name for the product -- `quotationToSide` reads `rawName` straight off
   * `productName`, because the ERP derives one from the other. Handing back
   * only the code let a simulation build a line naming one substance and
   * coding another, which no real quotation can be.
   *
   * An empty code with an empty name is the deliberate "nothing selected"
   * case: a quotation line with no resolved product is a real thing to verification.
   */
  readonly onPick: (selection: { erpCode: string; productName: string }) => void
}

export function ProductPicker({ label, suggestion, value, onPick }: ProductPickerProps) {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState(suggestion)
  const [candidates, setCandidates] = useState<CatalogCandidate[]>([])
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [seeding, setSeeding] = useState(false)
  /** Bumped to re-run the search after seeding, without retyping the term. */
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!open) return undefined
    let live = true
    // Debounced: a search per keystroke against a trigram index is a query per
    // keystroke against the pharmacy's own hardware in production.
    const timer = setTimeout(async () => {
      setSearching(true)
      try {
        const result = await api.searchCatalog(term)
        if (live) {
          setCandidates(result.candidates)
          setError(undefined)
        }
      } catch (cause) {
        if (live) setError(cause instanceof Error ? cause.message : 'falha na busca')
      } finally {
        if (live) setSearching(false)
      }
    }, 250)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [term, open, attempt])

  if (!open) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-tSmall text-text-weak">
          {value === '' ? 'Sem produto selecionado' : value}
        </span>
        <Button
          variant="tertiary"
          tone="brand"
          size="small"
          onClick={() => {
            setTerm(suggestion)
            setOpen(true)
          }}
        >
          {value === '' ? 'Escolher produto' : 'Trocar'}
        </Button>
      </div>
    )
  }

  return (
    <div className="border-stroke-weak flex w-full flex-col gap-2 rounded-[8px] border p-3">
      <Input
        size="small"
        label={label}
        value={term}
        autoFocus
        onChange={(event) => setTerm(event.target.value)}
      />

      {error !== undefined && <p className="text-tSmall text-text-error">{error}</p>}
      {searching && <p className="text-tSmall text-text-weak">Buscando…</p>}
      {/*
        The demo catalogue is offered here rather than on the simulations page,
        because this is the only moment anybody needs it: a search came back
        empty and there is nothing to pick. On the index it was a banner asking
        a question nobody had yet, every visit, forever after the one click
        that answered it.

        It is a development affordance, not a feature -- a account with a real
        ERP has a synced catalogue and will never see this branch.
      */}
      {!searching && candidates.length === 0 && (
        <div className="flex flex-col items-start gap-1">
          <p className="text-tSmall text-text-weak">Nada encontrado para “{term}”.</p>
          <Button
            variant="tertiary"
            tone="brand"
            size="small"
            disabled={seeding}
            onClick={async () => {
              setSeeding(true)
              try {
                const stats = await api.seedDemoCatalog()
                setError(undefined)
                setAttempt((n) => n + 1)
                setTerm((t) => t)
                console.info(`catálogo de demonstração: ${stats.products} produtos`)
              } catch (cause) {
                setError(cause instanceof Error ? cause.message : 'falha ao carregar o catálogo')
              } finally {
                setSeeding(false)
              }
            }}
          >
            {seeding ? 'Carregando…' : 'Carregar catálogo de demonstração'}
          </Button>
        </div>
      )}

      <ul className="flex flex-col gap-1">
        {candidates.map((candidate) => (
          <li key={candidate.erpCode}>
            <button
              type="button"
              className="hover:bg-fill-weak/40 flex w-full flex-col items-start rounded-[4px] p-2 text-left"
              onClick={() => {
                onPick({ erpCode: candidate.erpCode, productName: candidate.productName })
                setOpen(false)
              }}
            >
              <span className="text-tP text-text-strong">{candidate.productName}</span>
              <span className="text-tSmall text-text-weak">
                {candidate.erpCode} · {LAYER_LABEL[candidate.layer]}
                {/* The term that matched, when it is not the product's own
                    name. That is the whole reason a search for "Vitamina B12"
                    can return "Cianocobalamina", and hiding it would make the
                    result look like a mistake. */}
                {candidate.matchedTerm !== candidate.productName && ` — “${candidate.matchedTerm}”`}
              </span>
            </button>
          </li>
        ))}
      </ul>

      <div className="flex gap-2">
        <Button variant="tertiary" tone="neutral" size="small" onClick={() => setOpen(false)}>
          Cancelar
        </Button>
        {/*
          Leaving it blank is a real choice: a quotation line with no resolved
          product is a thing that happens, and a simulation that cannot express
          it cannot exercise what the verification does about it.
        */}
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          onClick={() => {
            onPick({ erpCode: '', productName: '' })
            setOpen(false)
          }}
        >
          Deixar em branco
        </Button>
      </div>
    </div>
  )
}
